import React, { Component, CSSProperties } from 'react';
import styles from './index.less';
import cns from 'classnames';
import { EventEmitter, InjectorService } from '@tencent/workbench';

const defaultProps = {
    value: '0',
    suffix: '',
    step: 1,
    toFixed: 0,
    min: 0,
    max: 100,
    allowDecimal: false,
    disabled: false,
};

export const lit = <V extends keyof any>(val: V): V => val;

export const convertNumber = (value: string | number, toFixed?: number): string | number => {
    if (value === '' || isNaN(Number(value))) return value;
    let result;
    if (toFixed) {
        const reg = new RegExp(`^\\d+(?:\\.\\d{0,${toFixed}})?`);
        const newVal = reg.exec(String(value));
        result = Number(newVal ? newVal : value);
    } else {
        result = parseInt(String(value), 10);
    }
    return result;
};

/**
 * @public
 * @deprecated
 * NumberInput 组件会移入 DUI，1.0 版本后，Form 扩展不再通过枚举提供此变量
 */
export const NumberInputFrom = {
    INPUT: lit('input'),
    ADD_BTN: lit('add-btn'),
    REDUCE_BTN: lit('reduce-btn'),
};
/**
 * @public
 * @deprecated
 * NumberInput 组件会移入 DUI，1.0 版本后，Form 扩展不再通过枚举提供此变量
 */
export type NumberInputFrom = (typeof NumberInputFrom)[keyof typeof NumberInputFrom];

export type INumberInputProps = {
    value?: string | number,
    defaultValue?: number,
    suffix?: string, // 单位
    min: number,
    max: number,
    step: number,
    toFixed?: number,
    allowDecimal?: boolean,
    disabled?: boolean,
    maxLength?: number;
    inputStyle?: CSSProperties;
    style?: CSSProperties;
    placeholder?: string;
    noButtons?: boolean;
    onChange?: (value: string, from?: NumberInputFrom) => void;
    onFocus?: () => void;
    onBlur?: () => void;
} & typeof defaultProps;

type INumberInputState = {
    value: string | number,
};

const MAX_LENGTH = 50;

// TODO: 移入 DUI

/**
 * 输入框，包含增加，减少按钮
 */
export class NumberInput extends Component<INumberInputProps, INumberInputState> {
    static defaultProps = defaultProps;

    private oldValue: string | number;
    private startValue: string;
    private inputRef;
    private eventEmitter: EventEmitter;
    private isInputting: boolean;
    private hasSuffix: boolean;
    private inputValue: string;
    private isFocus: boolean;

    constructor(props) {
        super(props);
        this.oldValue = this.getFixedValue(this.props.value);
        this.state = {
            value: this.oldValue,
        };
        this.eventEmitter = InjectorService.get(EventEmitter);
        this.isInputting = false;
    }

    // eslint-disable-next-line react/no-deprecated
    componentWillReceiveProps(nextProps) {
        const { value } = nextProps;
        let fixedValue = value;
        if (this.isInputting) {
            fixedValue = this.inputValue;
        } else {
            fixedValue = this.getFixedValue(value);
        }
        this.setState({
            value: fixedValue,
        });
    }

    public render() {
        const {
            maxLength, disabled, inputStyle, style,
            value, min, max, noButtons = false,
        } = this.props;
        const maxLen = typeof (maxLength) === 'undefined' ? MAX_LENGTH : maxLength;
        return (
            <div className={cns(styles['number-input-wrapper'], {
                [styles['disabled-number-input-wrapper']]: disabled,
            })} style={style}>
                <input
                    disabled={disabled}
                    className={styles['number-input']}
                    maxLength={maxLen}
                    value={this.state.value}
                    onChange={this.handleValueChange}
                    ref={(ref) => {
                        this.inputRef = ref;
                    }}
                    onBlur={this.handleBlur}
                    onFocus={this.handleFocus}
                    placeholder={this.props.placeholder}
                    style={inputStyle}
                    role="spinbutton"
                    aria-valuemin={min}
                    aria-valuemax={max}
                />
                {!noButtons && <div className={styles['number-input-buttons']}>
                    <div className={styles['up-button']} onMouseDown={this.handleAddClick} style={parseFloat(value) >= max ? { opacity: 0.5, pointerEvents: 'none' } : {}} role="button" aria-label="Add">
                        <div className={styles.icon}>
                            <svg width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M2.84 0.213334C2.92 0.106667 3.08 0.106667 3.16 0.213333L5.76 3.68C5.85889 3.81185 5.76481 4 5.6 4H0.4C0.235191 4 0.141115 3.81185 0.24 3.68L2.84 0.213334Z" fill="#81868F"/>
                            </svg>
                        </div>
                    </div>
                    <div className={styles['down-button']} onMouseDown={this.handleReduceClick} style={parseFloat(value) <= min ? { opacity: 0.5, pointerEvents: 'none' } : {}} role="button" aria-label="Sub">
                        <div className={styles.icon}>
                            <svg width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M2.84 3.78667C2.92 3.89333 3.08 3.89333 3.16 3.78667L5.76 0.32C5.85889 0.188153 5.76481 0 5.6 0H0.4C0.235191 0 0.141115 0.188153 0.24 0.32L2.84 3.78667Z" fill="#81868F"/>
                            </svg>
                        </div>
                    </div>
                </div>}
            </div>
        );
    }

    private suffix = () => this.props.suffix;

    private handleValueChange = (e) => {
        const hasPoint = this.props.allowDecimal;
        // 为单位正则增加括号，例如 厘米？-> (厘米)？，避免单位是多个字符时匹配错误，下同
        const reg = new RegExp(`^-?\\d*${hasPoint ? '\\.?' : ''}\\d{0,15}(${this.suffix()})?$`);
        const { value } = e.target;
        const stopInput = this.getStopInputFromValue(value);
        if (!stopInput && value.match(reg)) {
            this.isInputting = true;
            this.hasSuffix = value.endsWith(this.suffix());
            if (hasPoint) {
                const nanReg = new RegExp(`^-?\\.(${this.suffix()})?$`);
                if (value.match(nanReg)) return; // 例如 .厘米，是错误格式，直接返回
            }
            this.inputValue = value;
            if (this.suffix() && value === this.suffix()) {
                this.setState({
                    value,
                });
                this.onChange(value, NumberInputFrom.INPUT);
                return;
            }
            const suffixReg = new RegExp(`^-?(${this.suffix()})?$`);
            if (!!value && value.match(suffixReg)) {
                this.setState({
                    value,
                });
                return;
            }
            this.oldValue = value;
            this.setState({
                value: this.oldValue,
            });
            this.onChange(value, NumberInputFrom.INPUT);
        }
    };

    private handleAddClick = (e) => {
        if (this.props.disabled) return;
        this.handleBtnClick(e);

        this.isInputting = false;
        const number = this.getNumber();
        if (typeof number === 'undefined') return;
        const { step } = this.props;
        let nextVal = number;
        if (number + step < this.props.min) {
            nextVal = this.props.min;
        } else if (number + step > this.props.max) {
            nextVal = this.props.max;
        } else  {
            nextVal = number + step;
            // 对于0.5 + 0.1 = 0.5999999的浮点数相加问题，floor会导致步长为小数的操作失败
            if (step < 1) {
                nextVal = Math.round(nextVal / step) * step;
            } else {
                nextVal = Math.floor(nextVal / step) * step; // 值始终为step的倍数
            }
        }
        if (this.props.toFixed) {
            nextVal = Number(nextVal.toFixed(this.props.toFixed));
        }
        const value = `${nextVal}${this.suffix()}`;
        this.oldValue = value;
        this.setState({
            value: this.oldValue,
        });

        this.onChange(value, NumberInputFrom.ADD_BTN);
    };

    private handleReduceClick = (e) => {
        if (this.props.disabled) return;
        this.handleBtnClick(e);

        this.isInputting = false;
        const number = this.getNumber();
        if (typeof number === 'undefined') return;
        const { step } = this.props;
        let nextVal = number;
        if (number - step < this.props.min) {
            nextVal = this.props.min;
        } else if (number - step > this.props.max) {
            nextVal = this.props.max;
        } else  {
            nextVal = number - step;
            // 解决浮点数相减问题
            if (step < 1) {
                nextVal = Math.round(nextVal / step) * step;
            } else {
                nextVal = Math.ceil(nextVal / step) * step; // 值始终为step的倍数
            }
        }
        if (this.props.toFixed) {
            nextVal = Number(nextVal.toFixed(this.props.toFixed));
        }
        const value = `${nextVal}${this.suffix()}`;
        this.oldValue = value;
        this.setState({
            value: this.oldValue,
        });
        this.onChange(value, NumberInputFrom.REDUCE_BTN);
    };

    private getNumber = (): number | undefined => {
        let number = parseFloat(this.inputRef.value);
        if (isNaN(number)) {
            if (typeof this.props.defaultValue === 'undefined') {
                return;
            }
            number = this.props.defaultValue;
        }
        return number;
    };

    private handleBlur = (e) => {
        this.isFocus = false;
        let { value } = e.target;
        this.isInputting = false;
        this.props.onBlur?.();
        this.eventEmitter.emit('$formNumberInputBlur');
        if (this.startValue === value) {
            return;
        }
        if (value === '') {
            if (typeof this.props.defaultValue !== 'undefined') {
                value = this.props.defaultValue;
                this.onChange(`${value}${this.suffix()}`, NumberInputFrom.INPUT);
            } else {
                this.onChange('', NumberInputFrom.INPUT);
            }
        } else {
            // 解决多个字符的单位匹配问题
            const reg = new RegExp(`^-?\\d*${this.props.allowDecimal ? '\\.?\\d*' : ''}(${this.suffix()})?$`);
            if (isNaN(parseFloat(value))) {
                value = 0;
            } else if (value?.match(reg)) {
                // value有正常的值，不使用oldValue
            } else {
                value = this.oldValue ? this.oldValue : 0;
            }
            value = parseFloat(value);
            const { min, max } = this.props;
            if (value <= min) {
                value = min;
            }
            if (value >= max) {
                value = max;
            }
            this.onChange(`${value}${this.suffix()}`, NumberInputFrom.INPUT);
        }
        this.setState({
            value: this.getFixedValue(value),
        });
    };

    private handleFocus = (e) => {
        this.isFocus = true;
        this.startValue = e.target.value;
        this.oldValue = e.target.value;
        this.props.onFocus?.();
        this.eventEmitter.emit('$formNumberInputFocus');
    };

    private onChange = (value, from: NumberInputFrom) => {
        this.props.onChange?.(value, from);
    };

    private getStopInputFromValue(value) {
        let stopInput = false;
        if (this.props.toFixed) {
            const n = parseFloat(value);
            const array = n.toString().split('.');
            const len = n.toString().length;
            if (array.length > 1) {
                stopInput = this.isDecimalsOk(array, value, len);
            }
            if (array.length === 1 && !isNaN(n) && value[len] === '.') {
                // 如果是整数，检查是否存在.00的情况
                stopInput = this.isIntegerOk(value, len);
            }
        }
        return stopInput;
    }

    private isIntegerOk(value, len) {
        let zeroSum = 0;
        while (value[len + zeroSum + 1] === '0') {
            zeroSum += 1;
        }
        return zeroSum > this.props.toFixed;
    }

    private isDecimalsOk(array, value, len) {
        const sn = array[1].length;
        if (sn > this.props.toFixed) {
            return true;
        }
        if (sn <= this.props.toFixed) { // 如果小数位满足条件，检查是否是.200的情况
            let zeroSum = 0;
            const restFixed = this.props.toFixed - sn;
            while (value[len + zeroSum] === '0') {
                zeroSum += 1;
            }
            if (zeroSum > restFixed) {
                return true;
            }
        }
        return false;
    }

    private getFixedValue(value: string | number) {
        const { suffix } = this.props;
        if (suffix) {
            if (value === '') {
                return '';
            }
            return String(value).indexOf(suffix) === -1
                ? convertNumber(value, this.props.toFixed) + suffix
                : value;
        }
        return convertNumber(value, this.props.toFixed);
    }

    private handleBtnClick(e) {
        e.preventDefault();
        if (!this.isFocus) {
            this.inputRef.focus();
        }
    }
}
